/**
 * Toaster Component
 * Toast notification container using Sonner
 */

import React from 'react';
import { Toaster as SonnerToaster, toast as sonnerToast } from 'sonner';
import { useUIStore, selectToasts, type Toast } from '@/stores/ui-store';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

/**
 * Toaster component that renders toast notifications
 * Uses Sonner for the toast container and integrates with ui-store
 */
export function Toaster() {
  const toasts = useUIStore(selectToasts);
  const { removeToast } = useUIStore();

  // Sync ui-store toasts with Sonner
  React.useEffect(() => {
    toasts.forEach((toast) => {
      // Show toast via Sonner if not already shown
      const toastId = toast.id;
      
      sonnerToast.custom(
        (t) => (
          <div
            className={cn(
              'flex w-full items-start gap-3 rounded-lg border p-4 shadow-lg',
              'bg-[#ffffff] border-[#e5e5e5]',
              toast.type === 'error' && 'border-[#000000]',
              toast.type === 'success' && 'border-[#000000]',
              toast.type === 'warning' && 'border-[#666666]',
              toast.type === 'info' && 'border-[#999999]'
            )}
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#000000]">
                {toast.title}
              </p>
              {toast.message && (
                <p className="text-sm text-[#666666] mt-1">
                  {toast.message}
                </p>
              )}
              {toast.action && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2 h-7 text-xs"
                  onClick={() => {
                    toast.action?.onClick();
                    removeToast(toastId);
                    sonnerToast.dismiss(t);
                  }}
                >
                  {toast.action.label}
                </Button>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 flex-shrink-0 -mr-2 -mt-2"
              onClick={() => {
                removeToast(toastId);
                sonnerToast.dismiss(t);
              }}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        ),
        {
          id: toastId,
          duration: toast.duration || 5000,
        }
      );
    });
  }, [toasts, removeToast]);

  return (
    <SonnerToaster
      position="bottom-right"
      toastOptions={{
        style: {
          background: '#ffffff',
          border: '1px solid #e5e5e5',
          color: '#000000',
        },
      }}
      // Monochrome styling
      icons={{
        success: <div className="w-4 h-4 rounded-full bg-[#000000]" />,
        error: <div className="w-4 h-4 rounded-full bg-[#000000]" />,
        warning: <div className="w-4 h-4 rounded-full bg-[#666666]" />,
        info: <div className="w-4 h-4 rounded-full bg-[#999999]" />,
        loading: <div className="w-4 h-4 rounded-full bg-[#e5e5e5] animate-pulse" />,
      }}
    />
  );
}

export default Toaster;
